import { motion } from "framer-motion";
import { resolveNamedIcon } from "@/lib/namedIcons";
import { MathRichText } from "@/lib/mathRichText";

export type StepItem = string | { icon?: string; text: string };

function stepLabel(item: StepItem): string {
  if (typeof item === "string") return item;
  if (item && typeof item === "object" && "text" in item) {
    return String(item.text ?? "");
  }
  return String(item);
}

function stepIcon(item: StepItem): string | undefined {
  if (typeof item === "string") return undefined;
  if (item && typeof item === "object" && "icon" in item) {
    return item.icon != null ? String(item.icon) : undefined;
  }
  return undefined;
}

interface Props {
  data: { type: "steps"; title?: string; items: StepItem[]; animation?: string };
  isChildTheme?: boolean;
}

export function ElementSteps({ data, isChildTheme }: Props) {
  const items = data.items ?? [];
  const child = !!isChildTheme;
  return (
    <motion.div
      className="space-y-2"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {data.title && (
        <div className="font-semibold text-slate-800">
          <MathRichText text={data.title} />
        </div>
      )}
      <ol className="list-none pl-0 space-y-2">
        {items.map((raw, i) => {
          const label = stepLabel(raw);
          const iconRaw = stepIcon(raw);
          const resolved =
            resolveNamedIcon(iconRaw, child) ||
            (iconRaw && /[^\u0000-\u007F]/.test(iconRaw) ? iconRaw : null);
          return (
            <motion.li
              key={i}
              className="flex gap-3 items-start"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.06 }}
            >
              <span
                className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-bold bg-gradient-to-br from-pink-400 to-purple-500"
                aria-hidden
              >
                {resolved ? (
                  <span className="text-base leading-none">{resolved}</span>
                ) : iconRaw && !child ? (
                  <span className="text-base leading-none">{iconRaw}</span>
                ) : (
                  i + 1
                )}
              </span>
              <span className="text-slate-700 text-sm pt-0.5">
                <MathRichText text={label} />
              </span>
            </motion.li>
          );
        })}
      </ol>
    </motion.div>
  );
}
